import React, { useCallback } from "react";
import classnames from "classnames";
import styles from "./PermissionsEditor.module.scss";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { useTranslation } from "react-i18next";
import { granularPermissionDomains } from "@whatslly/commons/platformPermissions";
import type { PermissionInstanceConfig, PermissionInstanceConfigs, granularPermissions } from "@whatslly/commons/platformPermissions";
import { hasExactUserPermission, toggleUserPermission, UserPermissions } from "@whatslly/commons/granularPermissions";
import { observer } from "mobx-react-lite";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import uniq from "lodash/uniq";

interface PermissionsProps {
  permissions: UserPermissions;
  isReadonly?: (permission: UserPermissions) => false | string;
  setPermissions: (permissions: UserPermissions) => void;
  instanceConfigs?: PermissionInstanceConfigs;
}

interface PermissionsEditorProps extends PermissionsProps {
  hideTitle?: boolean;
  optionalPermissions?: PermissionInstanceConfig[];
}

interface PermissionsRowProps extends PermissionsProps {
  instanceConfig: PermissionInstanceConfig;
}

const PermissionRow: React.FC<PermissionsRowProps> = observer(({ instanceConfig, isReadonly, permissions, setPermissions }) => {
  const { t } = useTranslation();

  const toggleBitmap = useCallback(
    (key: granularPermissions) => {
      if (isReadonly?.(UserPermissions[key])) {
        return;
      }
      setPermissions(toggleUserPermission({ permissions }, UserPermissions[key]));
    },
    [setPermissions, permissions, isReadonly]
  );

  return (
    <div className={styles.domainPermissions}>
      <span>{t(`user_management.invite_user_modal.granular_permissions.${instanceConfig}`)}</span>
      <div className={styles.permissions}>
        {granularPermissionDomains[instanceConfig].map((key) => (
          <Tooltip value={isReadonly?.(UserPermissions[key]) || undefined} key={key}>
            <div className={styles.permission} onClick={() => toggleBitmap(key)}>
              <span className={styles.checkbox}>
                <CheckBox
                  data-testid={`permission-${key}`}
                  paddingInline={"none"}
                  checked={hasExactUserPermission({ permissions }, UserPermissions[key])}
                  disabled={Boolean(isReadonly?.(UserPermissions[key]))}
                />
              </span>
              <span>{t(`user_management.invite_user_modal.granular_permissions.${key}`)}</span>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
});

export const PermissionsEditor: React.FC<PermissionsEditorProps> = observer(
  ({ optionalPermissions, hideTitle, isReadonly, permissions, setPermissions, instanceConfigs }) => {
    const { t } = useTranslation();

    return (
      <>
        {uniq(optionalPermissions ?? []).length && !hideTitle ? (
          <div className={classnames(styles.permissionTitle, styles.textSecondary)}>{t("user_management.invite_user_modal.permissions.title")}</div>
        ) : null}
        {uniq(optionalPermissions ?? []).map(
          (key) =>
            (!instanceConfigs || instanceConfigs[key]) && (
              <PermissionRow key={key} instanceConfig={key} permissions={permissions} setPermissions={setPermissions} isReadonly={isReadonly} />
            )
        )}
      </>
    );
  }
);
